/**
 * The ink-brush samples in the game (C0-VFX): which moves they replace, their swings for the
 * renderer (`SKILL_SWINGS`, converted from each sample's key timeline), and the effects pushed for
 * a cast, a release and a basic cut. Effects (render/effects.ts) calls these and skips its own
 * spectacle for the moves replaced.
 */
import { artOfSkill, type SkillDef } from '@nine/shared';
import type { Fx } from '../fx/registry';
import { drawDart, drawWarp, templateOf } from './kinds';
import { drawFlyingOne } from './swords';
import { DRAGON_FINISHER, DRAGON_FORMS, isDragon } from './dragon';
import { SHOWCASES } from './showcase';
import { drawImpact, setSparkShare, type Weight } from './spectacle';
import { tierIndex } from '../../graphics';
import { detailCast, detailHit, detailRelease } from './detail/fx';
import { brushOn } from './brush';
import { pixelBlood, pixelDart, pixelImpact } from './pixelHit';

/** Dev: which ink entry points fired since the last read (`__inkCalls()`), for the brush-look audit. */
const CALLS: Record<string, number> = {};
const called = (name: string): void => {
  if (import.meta.env?.DEV) CALLS[name] = (CALLS[name] ?? 0) + 1;
};
if (import.meta.env?.DEV && typeof window !== 'undefined')
  (window as unknown as { __inkCalls: () => Record<string, number> }).__inkCalls = () => {
    const r = { ...CALLS };
    for (const k of Object.keys(CALLS)) delete CALLS[k];
    return r;
  };
const dirOf = (sx: number, tx: number): number => (tx < sx ? -1 : 1);
import type { MotionId } from '../motions';
import {
  drawBlood,
  drawCut,
  drawGuardBreak,
  INK_SAMPLES,
  inkSampleOf,
  type BloodHit,
  type InkLayer,
  type InkSample,
  type InkScene,
} from './samples';

/** Swing styles of the ink motions (render/skillFx.ts `SwingStyle`). */
export type WxStyle =
  | 'wxCleave'
  | 'wxRise'
  | 'wxBreak'
  | 'wxPalm'
  | 'wxQi'
  | 'wxHeal'
  | 'wxUlt'
  | 'wxThrust'
  | 'wxSlam'
  | 'wxPower'
  | 'wxBuff'
  | 'wxStomp'
  | 'wxUltShow'
  | 'wxUltPalmShow'
  | 'wxUltDragonShow';

/** The motion each replaced move plays (by skill id). */
export const WX_MOTION: Readonly<Record<string, WxStyle>> = {
  'ns-pierce': 'wxBreak',
  'cp-wave': 'wxPalm',
  'bs-gust': 'wxQi',
  'ns-calm': 'wxHeal',
  // The ultimates' showcases (render/ink/showcase.ts).
  'ns-heaven': 'wxUltShow',
  'cp-heaven': 'wxUltPalmShow',
};

interface Swing {
  id: MotionId;
  pts: readonly (readonly [number, number])[];
  contact: number;
  strike: number;
  reach: number;
  hop?: { from: number; to: number; h: number };
}

const keyAt = (pts: readonly (readonly [number, number])[], t: number): number => {
  for (let i = 1; i < pts.length; i++) {
    const [t0, k0] = pts[i - 1]!;
    const [t1, k1] = pts[i]!;
    if (t <= t1) return k0 + (k1 - k0) * Math.max(0, Math.min(1, (t - t0) / Math.max(1e-6, t1 - t0)));
  }
  return pts[pts.length - 1]![1];
};

/** A sample's motion from its release on, as a swing of `k` (0–1) over its remaining seconds. */
function swingOf(s: InkSample, reach: number): { swing: Swing; dur: number } {
  const [id, pts] = s.motions[0]!;
  const end = pts[pts.length - 1]![0];
  const dur = end - s.release;
  // A charged move (one released after a wind-up the cast showed) starts from its wind-up key: a
  // swing that comes out of the cast skips to `pre` (skillFx.ts `skillPose`), so the first stretch
  // only plays if the move is used uncharged.
  const PRE = 0.15;
  const out: [number, number][] = [[0, 0]];
  if (s.release > 0) {
    out.push([PRE, keyAt(pts, s.release)]);
    for (const [t, k] of pts) if (t > s.release) out.push([PRE + ((1 - PRE) * (t - s.release)) / dur, k]);
  } else for (const [t, k] of pts) if (t > 0) out.push([t / dur, k]);
  const hit = s.hits[0] ?? end;
  return {
    swing: {
      id: id as MotionId,
      pts: out,
      contact: s.release > 0 ? PRE + ((1 - PRE) * (hit - s.release)) / dur : hit / dur,
      strike: keyAt(pts, hit),
      reach,
    },
    dur,
  };
}

const sample = (id: InkSample['id']) => INK_SAMPLES.find((s) => s.id === id)!;
const REACH: Record<string, number> = { break: 5, palm: 5, qi: 3, heal: 0, ult: 7, 'show-sword': 7, 'show-palm': 0, 'show-dragon': 2 };
/** The showcases' leaps, as [from, to, height] seconds into the move (the renderer's hop). */
const HOPS: Record<string, readonly [number, number, number]> = {
  'show-sword': [0.86, 1.76, 38],
  'show-palm': [0.92, 1.52, 34],
  'show-dragon': [1.0, 1.6, 30],
};

/** The skill swings of the ink motions, and the least time each is shown for (`MIN_SWING`). */
export const WX_SWINGS: Record<string, Swing> = {};
export const WX_MIN_SWING: Record<string, number> = {};
for (const s of [...Object.keys(WX_MOTION).map((id) => inkSampleOf(id)!), ...SHOWCASES]) {
  const style = s.motions[0]![0];
  if (WX_SWINGS[style]) continue;
  const { swing, dur } = swingOf(s, REACH[s.id] ?? 3);
  const hop = HOPS[s.id];
  WX_SWINGS[style] = hop ? { ...swing, hop: { from: hop[0] / dur, to: hop[1] / dur, h: hop[2] } } : swing;
  WX_MIN_SWING[style] = dur;
}

/**
 * The other moves' re-keyed motions (render/motions/ink.ts), as key timelines in seconds: their
 * swings, with the blow at `hit` s and a lift off the ground over [`hop`] s for the leaps.
 */
const KIND_TIMELINES: Readonly<
  Record<string, { pts: readonly (readonly [number, number])[]; hit: number; reach: number; hop?: [number, number, number] }>
> = {
  wxThrust: {
    pts: [[0, 0], [0.06, 1], [0.13, 2], [0.2, 3], [0.26, 4], [0.3, 5], [0.35, 6], [0.46, 7], [0.58, 8], [0.7, 9]],
    hit: 0.3,
    reach: 6,
  },
  wxSlam: {
    pts: [[0, 0], [0.06, 1], [0.14, 2], [0.22, 3], [0.32, 4], [0.4, 5], [0.48, 6], [0.55, 7], [0.68, 8], [0.8, 9], [0.92, 10]],
    hit: 0.48,
    reach: 6,
    hop: [0.24, 0.48, 14],
  },
  wxPower: {
    pts: [[0, 0], [0.06, 1], [0.14, 2], [0.24, 3], [0.34, 4], [0.42, 5], [0.52, 6], [0.64, 7], [0.68, 8], [0.72, 9], [0.8, 10], [0.95, 11], [1.05, 12], [1.15, 13], [1.25, 14]],
    hit: 0.72,
    reach: 7,
    hop: [0.3, 0.5, 6],
  },
  wxBuff: {
    pts: [[0, 0], [0.1, 1], [0.2, 2], [0.32, 3], [0.45, 4], [0.58, 5], [0.66, 6], [0.74, 7], [0.84, 8]],
    hit: 0.84,
    reach: 0,
  },
  wxStomp: {
    pts: [[0, 0], [0.08, 1], [0.2, 2], [0.3, 3], [0.36, 4], [0.5, 5], [0.68, 6], [0.84, 7], [0.96, 8]],
    hit: 0.36,
    reach: 0,
  },
};
for (const [style, k] of Object.entries(KIND_TIMELINES)) {
  const end = k.pts[k.pts.length - 1]![0];
  WX_SWINGS[style] = {
    id: style as MotionId,
    pts: k.pts.map(([t, key]) => [t / end, key] as const),
    contact: k.hit / end,
    strike: keyAt(k.pts, k.hit),
    reach: k.reach,
    ...(k.hop ? { hop: { from: k.hop[0] / end, to: k.hop[1] / end, h: k.hop[2] } } : {}),
  };
  WX_MIN_SWING[style] = end;
}

/** The re-keyed motion each template plays (the rig figures; beasts keep their stills). */
const TEMPLATE_STYLE: Readonly<Partial<Record<string, WxStyle>>> = {
  cut: 'wxCleave',
  rise: 'wxRise',
  thrust: 'wxThrust',
  palm: 'wxPalm',
  qi: 'wxQi',
  area: 'wxSlam',
  break: 'wxBreak',
  power: 'wxPower',
  heal: 'wxHeal',
  buff: 'wxBuff',
  // The sword spectacle (C0-VFX-PREP) keeps the motion of the template it replaces.
  beam: 'wxThrust',
  flying: 'wxQi',
  rain: 'wxSlam',
  giant: 'wxPower',
};

/**
 * The ink motion a move plays, by its template (null: keep its own — a spin, a fan's or a spear's
 * swing, a warp chain, or a creature's).
 */
/** The golden dragon palm's moves (dragonPalm/data.ts) and the motion each plays. */
const DRAGON_MOTION: Readonly<Record<string, WxStyle>> = {
  'dp9-burst': 'wxPalm',
  'dp9-lance': 'wxPalm',
  'dp9-twin': 'wxQi',
  'dp9-rend': 'wxBreak',
  'dp9-stomp': 'wxStomp',
  'dp9-whirl': 'wxPalm',
  'dp9-double': 'wxPalm',
  'dp9-sky': 'wxUltDragonShow',
};

export function wxMotionOf(s: SkillDef, category: Category, motion: string, w: string): WxStyle | null {
  if (DRAGON_MOTION[s.id]) return DRAGON_MOTION[s.id]!;
  if (s.spin || s.blink || w === 'fan' || w === 'spear' || w === 'polearm' || w === 'claw') return null;
  if (motion === 'spin' || motion === 'fan' || motion === 'spear') return null;
  return TEMPLATE_STYLE[templateOf(s, category, motion).t.id] ?? null;
}

/**
 * The basic chain's cuts as `[k, key]` points over the swing (renderer.ts), for a wind-up held to
 * `pre` and the blade meeting the foe at `kc`: the long ink cuts start from their wind-up key.
 */
export function wxCutPts(style: 'wxCleave' | 'wxRise', pre: number, kc: number): [number, number][] {
  return style === 'wxCleave'
    ? [
        [0, 2],
        [pre, 3],
        [pre + (kc - pre) * 0.5, 4],
        [kc, 5],
        [kc + (1 - kc) * 0.3, 6],
        [kc + (1 - kc) * 0.65, 7],
        [kc + (1 - kc) * 0.85, 8],
        [1, 9],
      ]
    : [
        [0, 1],
        [pre, 2],
        [pre + (kc - pre) * 0.6, 3],
        [kc, 3.5],
        [kc + (1 - kc) * 0.3, 5],
        [kc + (1 - kc) * 0.6, 6],
        [kc + (1 - kc) * 0.85, 7],
        [1, 8],
      ];
}
/** The key a basic cut freezes on for the hit-stop. */
export const WX_CUT_STRIKE = { wxCleave: 5, wxRise: 3.5 } as const;

type CustomFx = Extract<Fx, { type: 'custom' }> & { ground?: boolean };

/** Where a move plays out on screen: the caster's feet, facing, and the foe relative to them. */
function scene(sx: number, sy: number, tx: number, ty: number, has: boolean): InkScene {
  const flip = has ? tx < sx : false;
  const D = has ? Math.max(28, Math.abs(tx - sx)) : 64;
  // The guard ring is drawn by `inkGuardBreak`, only when a guard really breaks.
  // Blood comes from the real hits (`inkHitBlood`), not the sample's timeline.
  return { x: sx, y: sy, flip, D, Dr: has ? Math.abs(tx - sx) : undefined, tdy: has ? ty - sy : 0, guard: false, blood: false };
}

/** Both layers of a sample from time `from` of its timeline, for `life` seconds. */
function push(fx: Fx[], s: InkSample, sc: InkScene, from: number, life: number): void {
  for (const layer of ['ground', 'over'] as InkLayer[]) {
    const f: CustomFx = {
      type: 'custom',
      x: sc.x,
      y: sc.y,
      t: 0,
      life,
      ground: layer === 'ground',
      // Drawn where the move was cast (the world's offset added each frame).
      draw: (g, _x, _y, k, ox, oy) =>
        s.draw(g, from + k * life, { ...sc, x: sc.x + ox, y: sc.y + oy }, layer),
    };
    fx.push(f);
  }
}

/** A replaced move's charge (its wind-up before the release), if it has one. Returns whether it did. */
export function inkCast(fx: Fx[], skillId: string, sx: number, sy: number, tx: number, ty: number, has: boolean): boolean {
  called('inkCast');
  // Brush look off: the move's wind-up shows the game's own gathering tell (effects.ts castStart).
  if (!brushOn()) return false;
  const s = inkSampleOf(skillId);
  if (!s || s.release <= 0) return !!s;
  push(fx, s, scene(sx, sy, tx, ty, has), 0, s.release);
  return true;
}

/** A replaced move released: its effect from the release on. Returns whether the move is an ink sample. */
export function inkRelease(fx: Fx[], skillId: string, sx: number, sy: number, tx: number, ty: number, has: boolean): boolean {
  const s = inkSampleOf(skillId);
  if (!s) return false;
  called('inkRelease');
  const sc = scene(sx, sy, tx, ty, has);
  push(fx, s, sc, s.release, s.life - s.release);
  // C0-SKILLFX: the move's detail layer (render/ink/detail/), its blow where the sample's falls.
  detailRelease(fx, skillId, sc, Math.max(0, (s.hits[0] ?? s.release) - s.release));
  return true;
}

/** A custom effect drawn for `life` seconds with `draw(g, seconds, scene)` (the world's offset added). */
function timed(fx: Fx[], sc: InkScene, life: number, draw: (g: CanvasRenderingContext2D, t: number, sc: InkScene) => void): void {
  const f: CustomFx = {
    type: 'custom',
    x: sc.x,
    y: sc.y,
    t: 0,
    life,
    draw: (g, _x, _y, k, ox, oy) => draw(g, k * life, { ...sc, x: sc.x + ox, y: sc.y + oy }),
  };
  fx.push(f);
}

/** A basic sword cut's ink trail, the blade meeting the foe `contact` seconds in. */
export function inkCut(
  fx: Fx[],
  which: 1 | 2,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  has: boolean,
  facingLeft: boolean,
  contact: number,
): void {
  called('inkCut');
  const sc = { ...scene(sx, sy, tx, ty, has), flip: has ? tx < sx : facingLeft };
  timed(fx, sc, contact + 0.5, (g, t, s) => drawCut(g, which, t - contact, s, 'trail'));
}

/** A basic cut landing: the cut bleeds along the blade's path (or, with blood off, a flash and ink). */
export function inkCutHit(fx: Fx[], which: 1 | 2, sx: number, sy: number, tx: number, ty: number, gore: boolean): void {
  called('inkCutHit');
  // Brush look off: plain pixel blood (the impact star comes from `inkImpact`, called with it).
  if (!brushOn()) return pixelBlood(fx, tx, ty, dirOf(sx, tx), gore);
  timed(fx, { ...scene(sx, sy, tx, ty, true), noGore: !gore }, 0.9, (g, t, s) => drawCut(g, which, t, s, 'hit'));
}

/** How each sample's blow bleeds when it lands: a cut, the great cut, or a spit for palm and qi. */
const HIT_BLOOD: Readonly<Record<string, BloodHit>> = {
  break: 'down',
  ult: 'big',
  palm: 'spit',
  qi: 'spit',
  'show-sword': 'big',
  'show-palm': 'spit',
  'show-dragon': 'spit',
};

/** A replaced move's blow landing (not guarded): its blood, or the clean flash with blood off. */
export function inkHitBlood(fx: Fx[], skillId: string, sx: number, sy: number, tx: number, ty: number, gore: boolean): void {
  called('inkHitBlood');
  detailHit(fx, skillId, scene(sx, sy, tx, ty, true));
  const hit = HIT_BLOOD[inkSampleOf(skillId)?.id ?? ''];
  if (!hit) return;
  if (!brushOn()) return pixelBlood(fx, tx, ty, dirOf(sx, tx), gore, hit === 'big');
  timed(fx, { ...scene(sx, sy, tx, ty, true), noGore: !gore }, 0.9, (g, t, s) => drawBlood(g, t, s, hit));
}

/** A replaced break shattering the foe's guard: the ring cracked and flying apart. */
export function inkGuardBreak(fx: Fx[], sx: number, sy: number, tx: number, ty: number): void {
  called('inkGuardBreak');
  // With the brush look off only the pixel guard shield cracking apart draws (its brush cracks and ring are gone).
  timed(fx, scene(sx, sy, tx, ty, true), 0.6, (g, t, s) => drawGuardBreak(g, t, s));
}

/** Whether a move is one of the ink samples. */
export const isInk = (skillId: string): boolean => !!inkSampleOf(skillId);

export { sample as inkSample };

// ── every other move, by kind (render/ink/kinds.ts) ──

/** Whether a move is drawn in ink (every move, since round 4: volleys as brush darts, warp chains as dash strokes). */
export const inkHandles = (_s: SkillDef): boolean => true;

type Category = Parameters<typeof templateOf>[1];

/** A move's template effect, its blow lined up with the motion's contact (`contact` s after the release). */
export function inkGeneric(
  fx: Fx[],
  s: SkillDef,
  category: Category,
  motion: string,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  has: boolean,
  contact: number,
): void {
  called('inkGeneric');
  const { t, tone } = templateOf(s, category, motion);
  const sc: InkScene = { ...scene(sx, sy, tx, ty, has), tone };
  const lead = Math.max(0, contact - t.hit);
  // A template whose blow comes later than the motion's contact starts further in, so its blow lands on the real one.
  const skip = Math.max(0, t.hit - contact);
  const life = lead + t.life - skip;
  for (const layer of ['ground', 'over'] as InkLayer[]) {
    const f: CustomFx = {
      type: 'custom',
      x: sx,
      y: sy,
      t: 0,
      life,
      ground: layer === 'ground',
      draw: (g, _x, _y, k, ox, oy) => t.draw(g, k * life - lead + skip, { ...sc, x: sc.x + ox, y: sc.y + oy }, layer),
    };
    fx.push(f);
  }
  detailRelease(fx, s.id, sc, contact);
}

/** A charged break's wind-up (the amber tell and drops), over its `windup` seconds. */
export function inkGenericCast(fx: Fx[], s: SkillDef, sx: number, sy: number, tx: number, ty: number, has: boolean): boolean {
  if (s.kind !== 'break' || s.windup <= 0 || !inkHandles(s)) return false;
  called('inkGenericCast');
  // Brush look off: the game's own gathering tell shows the wind-up (effects.ts castStart).
  if (!brushOn()) return false;
  const sc: InkScene = { ...scene(sx, sy, tx, ty, has), tone: 'break' };
  const br = sample('break');
  timed(fx, sc, s.windup, (g, t, x) => {
    br.draw(g, (t / s.windup) * br.release, x, 'ground');
    br.draw(g, (t / s.windup) * br.release, x, 'over');
  });
  detailCast(fx, s.id, sc, s.windup);
  return true;
}

/** A move's landed blow (not guarded): the template's blood, or the clean flash with blood off. */
export function inkGenericBlood(
  fx: Fx[],
  s: SkillDef,
  category: Category,
  motion: string,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  gore: boolean,
): void {
  called('inkGenericBlood');
  detailHit(fx, s.id, scene(sx, sy, tx, ty, true));
  const hit = isDragon(s.id) ? DRAGON_FORMS[s.id]!.blood : templateOf(s, category, motion).t.blood;
  if (!hit) return;
  if (!brushOn()) return pixelBlood(fx, tx, ty, dirOf(sx, tx), gore, hit === 'big');
  timed(fx, { ...scene(sx, sy, tx, ty, true), noGore: !gore }, 0.9, (g, t, x) => drawBlood(g, t, x, hit));
}

/** A volley: one brush dart per target, each landing `at` seconds after the release. */
export function inkVolley(
  fx: Fx[],
  s: SkillDef,
  sx: number,
  sy: number,
  shots: readonly { tx: number; ty: number; at: number }[],
): void {
  called('inkVolley');
  const sword = artOfSkill(s.id)?.weapon === 'sword';
  for (const v of shots) {
    // Brush look off: a plain pixel dart (the flown swords are pixel already).
    if (!sword && !brushOn()) {
      pixelDart(fx, sx, sy, v.tx, v.ty, v.at, s.fx === 'needles' ? 'jade' : 'steel');
      continue;
    }
    const sc: InkScene = { ...scene(sx, sy, v.tx, v.ty, true), tone: s.kind === 'power' ? 'power' : 'attack' };
    // Sword arts fly a sword per shot (御劍, C0-VFX-PREP); others keep the dart.
    if (sword) timed(fx, sc, v.at + 0.45, (g, t, x) => drawFlyingOne(g, t - v.at, x, sc.tone ?? 'attack'));
    else timed(fx, sc, v.at + 0.4, (g, t, x) => drawDart(g, t - v.at, x));
  }
}

/** A warp of a warp chain: the dash stroke from where the fighter was to where they are, and an ink cut. */
export function inkWarp(
  fx: Fx[],
  s: SkillDef,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  tx: number,
  ty: number,
  has: boolean,
  which: 1 | 2,
): void {
  called('inkWarp');
  const tone = s.kind === 'power' ? 'power' : s.kind === 'break' ? 'break' : 'attack';
  timed(fx, { ...scene(x0, y0, x1, y1, true), tone }, 0.5, (g, t, x) => drawWarp(g, t, x));
  const sc: InkScene = { ...scene(x1, y1, tx, ty, has), tone };
  timed(fx, sc, 0.6, (g, t, x) => drawCut(g, which, t - 0.04, x, 'trail'));
}

/**
 * A golden dragon palm move released: its dragons from the palm's contact (`contact` s after the release), a
 * volley's dragons landing at the world's times. Returns whether the move is one of the art's.
 */
export function inkDragon(
  fx: Fx[],
  s: SkillDef,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  has: boolean,
  contact: number,
  arrivals?: readonly number[],
): boolean {
  const form = DRAGON_FORMS[s.id];
  if (form) called('inkDragon');
  // A move with a showcase (the art's ultimate) is played by `inkRelease`.
  if (!form || inkSampleOf(s.id)) return false;
  pushForm(fx, form, scene(sx, sy, tx, ty, has), contact, arrivals?.map((a) => a - contact));
  return true;
}

/** The art's basic chain finisher: a small dragon from the palm. */
export function inkDragonFinisher(fx: Fx[], sx: number, sy: number, tx: number, ty: number, has: boolean, contact: number): void {
  pushForm(fx, DRAGON_FINISHER, scene(sx, sy, tx, ty, has), contact);
}

function pushForm(
  fx: Fx[],
  form: { life: number; draw: (g: CanvasRenderingContext2D, t: number, sc: InkScene, l: InkLayer, a?: readonly number[]) => void },
  sc: InkScene,
  lead: number,
  arrivals?: readonly number[],
): void {
  // From the release: the wind-up drops gather until the palm is out (`lead` s), then the dragons.
  const pre = Math.max(0, lead);
  const life = pre + form.life;
  for (const layer of ['ground', 'over'] as InkLayer[]) {
    const f: CustomFx = {
      type: 'custom',
      x: sc.x,
      y: sc.y,
      t: 0,
      life,
      ground: layer === 'ground',
      draw: (g, _x, _y, k, ox, oy) => form.draw(g, k * life - pre, { ...sc, x: sc.x + ox, y: sc.y + oy }, layer, arrivals),
    };
    fx.push(f);
  }
}

/**
 * A landed blow's spectacle (render/ink/spectacle.ts): flash, sparks, debris and ground decals by weight — light for
 * a plain cut, moderate for a break, heavy for a power move.
 */
export function inkImpact(fx: Fx[], kind: string, w: Weight, sx: number, sy: number, tx: number, ty: number): void {
  called('inkImpact');
  // Brush look off: the spectacle (ink decals, ink motes, debris) gives way to a plain pixel impact star and sparks.
  if (!brushOn()) return pixelImpact(fx, kind, w, tx, ty, dirOf(sx, tx));
  const sc = scene(sx, sy, tx, ty, true);
  // The scale pass's budget: half the sparks, debris and motes on the two low tiers (as the detail layer).
  setSparkShare(tierIndex() <= 1 ? 0.5 : 1);
  const k = (kind === 'heal' || kind === 'buff' ? 'attack' : kind) as 'attack' | 'break' | 'power';
  for (const layer of ['ground', 'over'] as InkLayer[]) {
    const f: CustomFx = {
      type: 'custom',
      x: sx,
      y: sy,
      t: 0,
      life: 1.8,
      ground: layer === 'ground',
      draw: (g, _x, _y, kk, ox, oy) => drawImpact(g, kk * 1.8, { ...sc, x: sc.x + ox, y: sc.y + oy }, layer, w, k),
    };
    fx.push(f);
  }
}
